/**
 * Client-side shapes for Gramática.
 *
 * These mirror `scripts/seed-gramatica-schema.ts`, which is the authoring
 * schema and the source of truth. They are restated rather than imported
 * because `scripts/` is build-time tooling that reads the filesystem and pulls
 * in Zod — none of which belongs in the browser bundle. The seed verifier is
 * what keeps the two honest: nothing reaches the database without matching the
 * schema first.
 */

/** Does Portuguese behave like her German, her English, both, or neither? */
export type Anchor = 'de' | 'en' | 'both' | 'neither';

/** Every explanation, in the two languages she reads. */
export type Bilingual = { de: string; en: string };

/** A [start, end) range into the example's `pt`, resolved at seed time. */
export type FocusSpan = [number, number];

export type LessonExample = {
  pt: string;
  focus: FocusSpan[];
  de: string;
  en: string;
};

export type GramaticaLesson = {
  n: number;
  slug: string;
  block: number;
  title: { pt: string; de: string; en: string };
  rule: Bilingual;
  anchor: Anchor;
  parallel: { pt: string; de: string | null; en: string | null } | null;
  /** "German made you do this; Portuguese does not." Seven lessons carry one. */
  relief: Bilingual | null;
  trap: Bilingual | null;
  table: { headers: string[]; rows: string[][] } | null;
  examples: LessonExample[];
  introduces: string[];
  requires: string[];
  exercise_count: number;
};

type ExerciseBase = {
  id: string;
  concepts_used: string[];
  /** True only where the accent IS the lesson. See src/apps/gramatica/answer.ts. */
  accent_sensitive: boolean;
  lesson_n: number;
  lesson_slug: string;
};

export type FillBlankExercise = ExerciseBase & {
  kind: 'fill_blank';
  prompt_pt: string;
  answer: string;
  accept: string[];
  hint: Bilingual | null;
};

export type ChoiceExercise = ExerciseBase & {
  kind: 'choice';
  prompt_pt: string | null;
  instruction: Bilingual | null;
  options: string[];
  answer: string;
};

export type TransformExercise = ExerciseBase & {
  kind: 'transform';
  given_pt: string;
  instruction: Bilingual;
  answer: string;
  accept: string[];
};

export type OrderExercise = ExerciseBase & {
  kind: 'order';
  chunks: string[];
  answer: string[];
  also_accept: string[][];
};

export type GramaticaPayload =
  | FillBlankExercise
  | ChoiceExercise
  | TransformExercise
  | OrderExercise;

/**
 * Display names for the ten blocks.
 *
 * The block NUMBER is curriculum data and lives in the database with each
 * lesson; only the name is presentation, so only the name is here.
 */
export const BLOCK_NAMES: Record<number, string> = {
  1: 'Getting a sentence out',
  2: 'Nouns, gender, pointing',
  3: 'ser, estar, and where things are',
  4: 'Numbers and time',
  5: 'Asking and answering',
  6: 'Having, wanting, liking',
  7: 'Irregular verbs',
  8: 'Future and ongoing',
  9: 'The past',
  10: 'Getting things done',
};
